import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import './Galleri.css';

const ADMIN_EMAILS = [
  'christian.c.nielsen@gmail.com',
  'Mollelysgaard@hotmail.com',
  'kasper.anthon.soerensen@gmail.com',
  'Martindyrmanhansen@gmail.com',
  'mlundk@gmail.com',
  'kildetoft@fastmail.com',
];

const Galleri = () => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [year, setYear] = useState('2024');
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState('');
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const lightboxRef = useRef();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUser(data?.user || null);
      setIsAdmin(data?.user && ADMIN_EMAILS.includes(data.user.email));
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user || null);
      setIsAdmin(session?.user && ADMIN_EMAILS.includes(session.user.email));
    });
    return () => listener?.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line
  }, [user]);

  async function fetchImages() {
    setLoading(true);
    let query = supabase.from('gallery').select('*').order('created_at', { ascending: false });
    if (!isAdmin) {
      query = query.eq('is_private', false);
    }
    const { data, error } = await query;
    if (!error && data) setImages(data);
    setLoading(false);
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) return setError('Vælg et billede');
    setUploading(true);
    setError('');
    const filename = `${Date.now()}_${file.name}`;
    const { data: storageData, error: storageError } = await supabase.storage.from('gallery-images').upload(filename, file, { upsert: true });
    if (storageError) {
      setError('Fejl ved upload: ' + storageError.message);
      setUploading(false);
      return;
    }
    const { publicUrl } = supabase.storage.from('gallery-images').getPublicUrl(filename).data;
    const { error: dbError } = await supabase.from('gallery').insert({
      url: publicUrl,
      year,
      is_private: isPrivate,
      uploaded_by: user.email,
    });
    if (dbError) setError('Fejl ved database: ' + dbError.message);
    setUploading(false);
    setFile(null);
    fetchImages();
  }

  async function handleDelete(img) {
    if (!window.confirm('Er du sikker på at du vil slette dette billede?')) return;
    // Slet fra storage
    const path = img.url.split('/').pop();
    await supabase.storage.from('gallery-images').remove([path]);
    // Slet fra database
    await supabase.from('gallery').delete().eq('id', img.id);
    fetchImages();
    if (lightboxIdx !== null) setLightboxIdx(null);
  }

  function openLightbox(idx) {
    setLightboxIdx(idx);
  }
  function closeLightbox() {
    setLightboxIdx(null);
  }
  function nextImg() {
    if (lightboxIdx === null) return;
    setLightboxIdx((lightboxIdx + 1) % images.length);
  }
  function prevImg() {
    if (lightboxIdx === null) return;
    setLightboxIdx((lightboxIdx - 1 + images.length) % images.length);
  }

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIdx === null) return;
    function onKey(e) {
      if (e.key === 'ArrowRight') nextImg();
      if (e.key === 'ArrowLeft') prevImg();
      if (e.key === 'Escape') closeLightbox();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [lightboxIdx, images.length]);

  // Touch navigation (swipe)
  function onTouchStart(e) {
    setTouchStartX(e.touches[0].clientX);
  }
  function onTouchEnd(e) {
    if (touchStartX === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (dx > 50) prevImg();
    if (dx < -50) nextImg();
    setTouchStartX(null);
  }

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2016; y <= currentYear; y++) years.unshift(y);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <h1 style={{ textAlign: 'center', marginBottom: 24 }}>Galleri</h1>
      {isAdmin && (
        <form onSubmit={handleUpload} style={{ marginBottom: 32, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} />
          <label style={{ fontWeight: 500 }}>
            JULEGOLF-år:
            <select value={year} onChange={e => setYear(e.target.value)} style={{ marginLeft: 8 }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} />
            Privat
            <span style={{ fontSize: 13, color: '#888', marginLeft: 4 }}>(kun synligt for julegolfere, ikke offentligheden)</span>
          </label>
          <button type="submit" disabled={uploading}>{uploading ? 'Uploader...' : 'Upload billede'}</button>
          {error && <span style={{ color: 'red', marginLeft: 8 }}>{error}</span>}
        </form>
      )}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#888' }}>Indlæser billeder...</div>
      ) : (
        <div className="galleri-grid">
          {images.map((img, idx) => (
            <div key={img.id} className="galleri-card">
              <img src={img.url} alt={img.year} onClick={() => openLightbox(idx)} style={{ cursor: 'pointer' }} />
              <div className="galleri-meta">
                <span>{img.year}</span>
                {img.is_private && isAdmin && <span className="galleri-private">Privat</span>}
                {isAdmin && (
                  <button onClick={() => handleDelete(img)} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#e63946', fontWeight: 700, cursor: 'pointer', fontSize: 18 }}>🗑️</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Lightbox/modal visning */}
      {lightboxIdx !== null && images[lightboxIdx] && (
        <div className="galleri-lightbox" ref={lightboxRef} tabIndex={-1}
          onClick={closeLightbox}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{ position: 'fixed', zIndex: 2000, top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button onClick={e => { e.stopPropagation(); prevImg(); }} style={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)', fontSize: 36, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', zIndex: 10 }}>&lt;</button>
          <img src={images[lightboxIdx].url} alt={images[lightboxIdx].year} style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 16, boxShadow: '0 4px 32px #000a' }} onClick={e => e.stopPropagation()} />
          <button onClick={e => { e.stopPropagation(); nextImg(); }} style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', fontSize: 36, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', zIndex: 10 }}>&gt;</button>
          <button onClick={e => { e.stopPropagation(); closeLightbox(); }} style={{ position: 'absolute', top: 24, right: 24, fontSize: 32, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', zIndex: 10 }}>×</button>
        </div>
      )}
    </div>
  );
};

export default Galleri; 